import { useId, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { BankAccountRow } from '@/components/account/BankAccountRow.js';
import { BankSelect } from '@/components/account/BankSelect.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import {
  fetchBankAccounts,
  createBankAccount,
  fetchMemberMe,
  setDefaultBankAccount,
  MEMBER_QUERY_KEYS,
} from '@/features/member/api.js';
import { useBankCurrency, useBankMasters } from '@/features/finance/api.js';
import { bankLabel } from '@/features/finance/bank-label.js';
import { BANK_ACCOUNT_NO_RE } from '@/features/auth/register-rules.js';
import { mapApiError } from '@/lib/api/error.js';
import { useAuthStore } from '@/stores/auth.js';

function makeAddBankSchema(t: (key: string) => string) {
  return z.object({
    bankCode: z.string().min(1, t('addBank.bankCodeRequired')),
    accountNumber: z.string().regex(BANK_ACCOUNT_NO_RE, t('addBank.accountNumberInvalid')),
    isDefault: z.boolean().optional(),
  });
}
type AddBank = z.infer<ReturnType<typeof makeAddBankSchema>>;

function AddBankModal({
  memberId,
  onClose,
  onSuccess,
}: {
  memberId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('profile');
  const addBankSchema = useMemo(() => makeAddBankSchema(t), [t]);
  const accountNumberId = useId();
  const bankCurrency = useBankCurrency();
  const banksQuery = useBankMasters({ currency: bankCurrency });
  const banks = banksQuery.data?.items ?? [];

  // Account holder name is never typed by hand — it's derived from the
  // member's own profile (first/last name), falling back to their username
  // when neither is set. Same fallback used at registration (_auth.register.tsx).
  const memberQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.me,
    queryFn: fetchMemberMe,
  });
  const member = memberQuery.data?.member;
  const accountName =
    [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim() ||
    member?.username ||
    '';

  const form = useForm<AddBank>({
    defaultValues: { bankCode: '', accountNumber: '', isDefault: false },
    onSubmit: async ({ value }) => {
      const parsed = addBankSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0]?.message ?? t('common.validationError'));
        return;
      }
      await addBankMutation.mutateAsync(parsed.data);
    },
  });

  const addBankMutation = useMutation({
    mutationFn: (values: AddBank) =>
      createBankAccount(memberId, {
        bankCode: values.bankCode.toUpperCase(),
        accountNumber: values.accountNumber,
        accountName,
        isDefault: values.isDefault,
      }),
    onSuccess: () => {
      toast.success(t('addBank.success'));
      onSuccess();
      onClose();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogTitle>{t('addBank.title')}</DialogTitle>
        <form
          onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }}
          className="flex flex-col gap-3"
        >
          <form.Field name="bankCode" validators={{ onChange: ({ value }) => {
            const r = addBankSchema.shape.bankCode.safeParse(value);
            return r.success ? undefined : r.error.errors[0]?.message;
          }}}>
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('addBank.bankCodeLabel')}</label>
                <BankSelect
                  banks={banks}
                  value={field.state.value}
                  onChange={(code) => field.handleChange(code.toUpperCase())}
                  onBlur={field.handleBlur}
                  loading={banksQuery.isPending}
                  placeholder={t('addBank.bankCodePlaceholder')}
                  loadingLabel={t('addBank.loading')}
                />
                {field.state.meta.errors.length > 0 && (
                  <span role="alert" className="text-[11px] text-[var(--danger)]">{field.state.meta.errors[0]}</span>
                )}
                {!banksQuery.isPending && banks.length === 0 && (
                  <span role="status" className="text-[11px] text-[var(--text-muted)]">
                    {t('addBank.noBanks', { currency: bankCurrency })}
                  </span>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="accountNumber" validators={{ onChange: ({ value }) => {
            const r = addBankSchema.shape.accountNumber.safeParse(value);
            return r.success ? undefined : r.error.errors[0]?.message;
          }}}>
            {(field) => {
              const invalid = field.state.meta.errors.length > 0;
              const errorId = `${accountNumberId}-error`;
              return (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor={accountNumberId} className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('addBank.accountNumberLabel')}</label>
                  <Input
                    id={accountNumberId}
                    placeholder={t('addBank.accountNumberPlaceholder')}
                    inputMode="numeric"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={invalid}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? errorId : undefined}
                  />
                  {invalid && (
                    <span id={errorId} role="alert" className="text-[11px] text-[var(--danger)]">{field.state.meta.errors[0]}</span>
                  )}
                </div>
              );
            }}
          </form.Field>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{t('addBank.accountNameLabel')}</label>
            <div className="h-11 px-3.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)]/40 flex items-center text-[14px] font-semibold text-[var(--text-secondary)] truncate">
              {memberQuery.isPending ? t('addBank.loading') : accountName}
            </div>
          </div>

          <form.Field name="isDefault">
            {(field) => (
              <label className="flex items-center gap-2.5 text-[13px] text-[var(--text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.state.value ?? false}
                  onChange={(e) => field.handleChange(e.target.checked)}
                  className="accent-[var(--primary)]"
                />
                {t('addBank.setDefault')}
              </label>
            )}
          </form.Field>

          <div className="flex gap-2 mt-2">
            <Button type="submit" glow fullWidth disabled={addBankMutation.isPending || memberQuery.isPending}>
              {addBankMutation.isPending ? t('addBank.submitting') : t('addBank.submit')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t('addBank.cancel')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Linked bank accounts — list + add/set-default/remove. Self-contained (reads
 * memberId from the auth store); rendered on both the profile and wallet pages.
 */
export function BankAccountsSection() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation('profile');
  const memberId = useAuthStore((s) => s.subject?.id ?? '');
  const bankCurrency = useBankCurrency();
  const bankMastersQuery = useBankMasters({ currency: bankCurrency });
  const bankByCode = useMemo(
    () => new Map((bankMastersQuery.data?.items ?? []).map((m) => [m.code, m])),
    [bankMastersQuery.data],
  );
  const [showAddBank, setShowAddBank] = useState(false);

  const bankAccountsQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.bankAccounts(memberId),
    queryFn: () => fetchBankAccounts(memberId),
    enabled: !!memberId,
    staleTime: 30_000,
  });
  const banks = bankAccountsQuery.data?.items ?? [];

  const setDefaultMutation = useMutation({
    mutationFn: (bankId: string) => setDefaultBankAccount(memberId, bankId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MEMBER_QUERY_KEYS.bankAccounts(memberId) });
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  return (
    <>
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
            {t('banks.title')}
          </h2>
          <Button size="sm" variant="secondary" onClick={() => setShowAddBank(true)}>
            {t('banks.add')}
          </Button>
        </div>
        {bankAccountsQuery.isPending ? (
          <div className="h-16 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
        ) : banks.length === 0 ? (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-6 text-center text-[var(--text-muted)] text-[13px]">
            {t('banks.empty')}
          </div>
        ) : (
          <Card className="overflow-hidden">
            {banks.map((b, i) => {
              const master = bankByCode.get(b.bankCode);
              return (
              <div key={b.id} className={i > 0 ? 'border-t border-[var(--border-subtle)]' : ''}>
                <BankAccountRow
                  bank={{
                    id: b.id,
                    bankCode: b.bankCode,
                    bankName: master ? bankLabel(master, i18n.language) : b.bankCode,
                    accountNumber: b.accountNumber,
                    accountName: b.accountName,
                    isDefault: b.isDefault,
                    verified: b.verified,
                  }}
                  onSetDefault={(id) => setDefaultMutation.mutate(id)}
                />
              </div>
              );
            })}
          </Card>
        )}
        <div className="text-[12px] text-[var(--text-muted)] px-1">
          {t('banks.hint')}
        </div>
      </section>

      {showAddBank && (
        <AddBankModal
          memberId={memberId}
          onClose={() => setShowAddBank(false)}
          onSuccess={() => qc.invalidateQueries({ queryKey: MEMBER_QUERY_KEYS.bankAccounts(memberId) })}
        />
      )}
    </>
  );
}
